import { BlockStack, Card, Combobox, FormLayout, Layout, Select, TextField } from '@shopify/polaris';
import { FC, useEffect, useMemo } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';

import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { NUMBER_REGEX } from '@/constants';
import { PATH } from '@/constants/path';
import { openErrorToast, openToast } from '@/helpers';

import { useCreateLocationMutation, useGetLocationsQuery, useUpdateLocationMutation } from '@/redux/query';
import {
  backupLocationSettingSelector,
  handleResetLocationSettings,
  handleSaveLocationSettings,
  handleSetIsChangeData,
  handleToggleDiscardModal,
  isChangeDataSelector,
  locationSettingSelector,
} from '@/redux/slice/location.slice';
import { handleSetSaveBar } from '@/redux/slice/savebar.slice';
import { IParamsApi } from '@/types/apis/params';
import { IResponseApi } from '@/types/apis/response';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country, State } from 'country-state-city';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import CustomerOptions from './CustomerOptions';
import { SelectStyled } from './styled';
import { requiredPronvinceCode } from '@/helpers/location';

export type TFormLocation = {
  name: string;
  country: string;
  address: string;
  address2: string;
  city: string;
  postalCode: string;
  phone: string;
  province: string;
};

export type FormLocationProps = {
  detailLocation: IResponseApi.IGetDetailLocation;
  refetGetDetailLocation: () => void;
};

const FormLocation: FC<FormLocationProps> = ({ detailLocation, refetGetDetailLocation }) => {
  const { t } = useTranslation('locations');
  const locationSettings = useSelector(locationSettingSelector);
  const backupLocationSettings = useSelector(backupLocationSettingSelector);
  const isChangeData = useSelector(isChangeDataSelector);
  const dispatch = useDispatch();
  const { id: locationId } = useParams();
  const [updateLocation, { isLoading: isLoadingUpdateLocation }] = useUpdateLocationMutation();
  const [createLocation, { isLoading: isLoadingCreateLocation }] = useCreateLocationMutation();
  const { refetch: refetchLocationData } = useGetLocationsQuery({ shop: config.shop });
  const navigate = useNavigate();

  const listCountries = useMemo(() => Country.getAllCountries(), []);

  const convertListCountries = useMemo(() => {
    return listCountries.map((item) => ({
      label: item.name,
      value: item.isoCode,
    }));
  }, [listCountries]);

  const listStates = useMemo(
    () => State.getStatesOfCountry(locationSettings?.address?.countryCode),
    [locationSettings?.address?.countryCode],
  );

  const convertListStates = useMemo(() => {
    return listStates.map((item) => ({
      label: item.name,
      value: item.isoCode,
    }));
  }, [listStates]);

  const normalizeState = (state: string) => {
    return state
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '');
  };

  const schema = z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t('addEditLocation.messageError.companyName') }),
    address: z
      .string()
      .trim()
      .min(1, { message: t('addEditLocation.messageError.address') }),
    country: z
      .string()
      .trim()
      .min(1, { message: t('addEditLocation.messageError.country') }),
    city: z.string().optional(),
    province: z.string().optional(),
    provinceCode: z.string().optional(),
    postalCode: z.string().optional(),
    address2: z.string().optional(),
    phone: z.string().optional(),
  });

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    clearErrors,
  } = useForm({
    defaultValues: {
      name: '',
      address: '',
      address2: '',
      city: '',
      province: '',
      provinceCode: '',
      postalCode: '',
      phone: '',
      country: '',
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (detailLocation) {
      reset({
        name: detailLocation?.data.name || '',
        address: detailLocation?.data.address?.address1 || '',
        address2: detailLocation?.data.address?.address2 || '',
        city: detailLocation?.data.address?.city || '',
        postalCode: detailLocation?.data.address?.postalCode || '',
        phone: detailLocation?.data.address?.phone || '',
        country: detailLocation?.data.address?.countryCode || '',
        province: detailLocation?.data.address.province || '',
        provinceCode: detailLocation?.data.address.provinceCode || '',
      });
    }
  }, [detailLocation, detailLocation?.data, reset]);

  const handleChangeSelectCountry = (value: string) => {
    const selectCountry = convertListCountries?.filter((item) => item.value === value)[0]?.label;
    setValue('city', '');
    setValue('province', '');
    dispatch(
      handleSaveLocationSettings({
        address: {
          ...locationSettings.address,
          country: selectCountry || '',
          countryCode: value,
        },
      }),
    );
  };

  const handleChangeSelectState = (value: string) => {
    const selectState = convertListStates?.filter((item) => item.value === value)[0]?.label;
    setValue('city', '');
    dispatch(
      handleSaveLocationSettings({
        address: {
          ...locationSettings.address,
          province: selectState || '',
          provinceCode: value,
        },
      }),
    );
  };

  const handleSaveLocations: SubmitHandler<TFormLocation> = async (data, event) => {
    event?.preventDefault();
    dispatch(handleSetSaveBar({ isLoadingSaveBar: true }));
    const dataSubmit: IParamsApi.ICreateLocation = {
      name: data.name.trim(),
      isEnablePickup: locationSettings.isEnablePickup,
      isEnableDelivery: locationSettings.isEnableDelivery,
      address: {
        address1: data.address.trim(),
        address2: data.address2.trim(),
        city: data.city || '',
        country: locationSettings.address.country,
        countryCode: locationSettings.address.countryCode,
        phone: data.phone,
        postalCode: data.postalCode.trim(),
        province: requiredPronvinceCode.includes(locationSettings?.address?.countryCode) ? locationSettings.address.province : '',
        provinceCode: requiredPronvinceCode.includes(locationSettings?.address?.countryCode)
          ? locationSettings.address.provinceCode
          : '',
        longitude: '',
        latitude: '',
      },
      shop: config.shop,
    };

    const dataUpdate = {
      ...dataSubmit,
      id: Number(locationId),
    };

    if (!locationId) {
      createLocation(dataSubmit)
        .unwrap()
        .then(() => {
          openToast(dispatch);
          reset({}, { keepValues: true });
          dispatch(
            handleSetSaveBar({
              isShowSaveBar: false,
              isLoadingSaveBar: false,
              isDisableSaveBar: false,
              isCheckSave: false,
              isCheckingTimeSlot: false,
            }),
          );
          dispatch(handleSetIsChangeData(false));
          dispatch(handleToggleDiscardModal(false));
          refetchLocationData();
          navigate(PATH.LOCATIONS.pathname);
        })
        .catch(() => {
          openErrorToast(dispatch, 'Failed create location');
        });
    } else {
      updateLocation(dataUpdate)
        .unwrap()
        .then(() => {
          openToast(dispatch);
          reset({}, { keepValues: true });
          dispatch(
            handleSetSaveBar({
              isShowSaveBar: false,
              isLoadingSaveBar: false,
              isDisableSaveBar: false,
              isCheckSave: false,
              isCheckingTimeSlot: false,
            }),
          );
          refetchLocationData();
          refetGetDetailLocation();
          dispatch(handleSetIsChangeData(false));
          dispatch(handleToggleDiscardModal(false));
          navigate(PATH.LOCATIONS.pathname);
        })
        .catch(() => {
          openErrorToast(dispatch, 'Failed update location');
        });
    }
  };

  const handleDiscardSave = () => {
    reset();
    clearErrors();
    dispatch(
      handleSetSaveBar({
        isShowSaveBar: false,
        isLoadingSaveBar: false,
        isDisableSaveBar: false,
        isCheckSave: false,
        isCheckingTimeSlot: false,
      }),
    );

    dispatch(handleResetLocationSettings());
  };

  const isChangeCustomerOptionsData = useMemo(() => {
    const { isEnableDelivery, isEnablePickup } = locationSettings;
    const { isEnableDelivery: isBackupEnableDelivery, isEnablePickup: isBackupEnablePickup } = backupLocationSettings;
    return !!isEnablePickup !== !!isBackupEnablePickup || !!isEnableDelivery !== !!isBackupEnableDelivery;
  }, [locationSettings, backupLocationSettings]);
  const isHaveChangeLocationData = Object.keys(dirtyFields).length > 0 || isChangeCustomerOptionsData;

  useEffect(() => {
    dispatch(handleSetIsChangeData(isHaveChangeLocationData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHaveChangeLocationData]);

  useEffect(() => {
    const currentState = locationSettings?.address?.province || detailLocation?.data?.address?.province || '';
    const matchingState = convertListStates?.find(
      (option) => normalizeState(option.label) === normalizeState(String(currentState)),
    );

    if (matchingState) {
      setValue('province', matchingState.value);
    } else {
      setValue('province', '');
    }
  }, [locationSettings?.address?.province, detailLocation?.data?.address?.province, convertListStates, setValue]);

  return (
    <>
      <AppSaveBar
        open={isChangeData}
        onSave={handleSubmit(handleSaveLocations)}
        onDiscard={handleDiscardSave}
        loading={isLoadingCreateLocation || isLoadingUpdateLocation}
      />
      <form>
        <BlockStack gap={'400'}>
          <Layout>
            <Layout.Section variant="oneThird">
              <BlockStack gap="200">
                <BoldText>{t('addEditLocation.locationDetail')}</BoldText>
                <RegularText>{t('addEditLocation.locationDetailDesc')}</RegularText>
              </BlockStack>
            </Layout.Section>
            <Layout.Section>
              <Card>
                <FormLayout>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        label={t('addEditLocation.companyName')}
                        requiredIndicator
                        value={value}
                        onChange={(value) => onChange(value)}
                        error={errors.name?.message || ''}
                        autoComplete="off"
                      />
                    )}
                  />
                  <Controller
                    name="country"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <>
                        <Select
                          label={t('addEditLocation.country')}
                          placeholder="Select a country"
                          options={convertListCountries}
                          onChange={(value) => {
                            handleChangeSelectCountry(value);
                            onChange(value);
                          }}
                          requiredIndicator
                          value={value}
                          error={errors.country?.message || ''}
                        />
                      </>
                    )}
                  />
                  <FormLayout.Group>
                    <Controller
                      name="address"
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Combobox
                          activator={
                            <Combobox.TextField
                              label={t('addEditLocation.address')}
                              requiredIndicator
                              value={value}
                              onBlur={() => {
                                setValue('address', value.trim());
                                onBlur();
                              }}
                              onChange={(value) => {
                                onChange(value);
                              }}
                              error={errors.address?.message || ''}
                              autoComplete="off"
                            />
                          }
                        />
                      )}
                    />
                    <Controller
                      name="address2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label={t('addEditLocation.apartment')}
                          value={value}
                          onChange={(value) => {
                            onChange(value);
                          }}
                          autoComplete="off"
                        />
                      )}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    {requiredPronvinceCode.includes(locationSettings?.address?.countryCode) && (
                      <Controller
                        name="province"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                          return (
                            <Select
                              label={t('addEditLocation.state')}
                              placeholder="Select state or province"
                              onChange={(value) => {
                                handleChangeSelectState(value);
                                onChange(value);
                              }}
                              value={value}
                              error={errors.city?.message || ''}
                              options={convertListStates}
                            />
                          );
                        }}
                      />
                    )}
                    <Controller
                      name="city"
                      control={control}
                      render={({ field: { ref, ...field } }) => (
                        <SelectStyled>
                          <TextField
                            {...field}
                            label="City"
                            placeholder="Enter city name"
                            onChange={(value) => {
                              setValue('city', value);
                              field.onChange(value);
                            }}
                            error={errors.city?.message || ''}
                            autoComplete="off"
                          />
                        </SelectStyled>
                      )}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Controller
                      name="postalCode"
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <TextField
                          label={t('addEditLocation.postalCode')}
                          value={value}
                          onBlur={() => {
                            setValue('postalCode', value.trim());
                            onBlur();
                          }}
                          onChange={(value) => {
                            onChange(value);
                          }}
                          error={errors.postalCode?.message}
                          autoComplete="off"
                        />
                      )}
                    />
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          label={t('addEditLocation.phoneNumber')}
                          value={value}
                          type="tel"
                          inputMode="numeric"
                          onChange={(value) => {
                            if (NUMBER_REGEX.test(value)) {
                              dispatch(
                                handleSaveLocationSettings({
                                  address: {
                                    ...locationSettings.address,
                                    phone: value.trim(),
                                  },
                                }),
                              );
                              onChange(value);
                            }
                          }}
                          autoComplete="off"
                        />
                      )}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card>
            </Layout.Section>
          </Layout>
          <CustomerOptions />
        </BlockStack>
      </form>
    </>
  );
};

export default FormLocation;
